import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Button,
  Typography,
  Chip,
  IconButton,
  TextField,
  InputAdornment
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Search as SearchIcon,
  Assignment as TaskIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { ApiService } from '../../services/ApiService';
import { usePermissions } from '../../hooks/usePermissions';
import { Permission, UserRole } from '../../types/user';
import { useAuth } from '../../context/SimpleAuthContext';
import { transformTasks } from '../../utils/dataTransform';

interface Task {
  id: string;
  title: string;
  description: string;
  status: 'pending' | 'in-progress' | 'completed' | 'blocked';
  priority: 'low' | 'medium' | 'high' | 'critical';
  assignee: string;
  dueDate: string;
  requirementId: string;
  requirementTitle?: string;
  createdAt: string;
  updatedAt: string;
}

interface TaskListResponse {
  data: Task[];
  total: number;
}

export default function TaskList() {
  const navigate = useNavigate();
  const { hasPermission, userRole } = usePermissions();
  const { user } = useAuth();
  const [tasks, setTasks] = useState<Task[]>([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [searchQuery, setSearchQuery] = useState('');
  const [totalCount, setTotalCount] = useState(0);
  
  // Check permissions
  const canCreate = hasPermission(Permission.TASK_CREATE);
  const canEdit = hasPermission(Permission.TASK_EDIT) || hasPermission(Permission.TASK_EDIT_OWN);
  const canDelete = hasPermission(Permission.TASK_DELETE);
  const isTaskImplementer = userRole === UserRole.TASK_IMPLEMENTER;

  useEffect(() => {
    fetchTasks();
  }, [page, rowsPerPage]);

  const fetchTasks = async () => {
    try {
      setLoading(true);
      const params: any = {
        page: page + 1,
        limit: rowsPerPage,
        search: searchQuery
      };
      
      // For Task Implementers, only fetch their own tasks
      if (isTaskImplementer && user?.name) {
        params.assignee = user.name;
      }
      
      const response = await ApiService.get<any>('/tasks', params);
      // Transform the tasks data
      let filteredTasks = response.data ? transformTasks(response.data) : [];
      
      // Additional client-side filtering for Task Implementers (in case API doesn't support it)
      if (isTaskImplementer && user?.name) {
        filteredTasks = filteredTasks.filter(task => task.assignee === user.name);
      }
      
      setTasks(filteredTasks);
      setTotalCount(response.total || 0);
    } catch (error) {
      console.error('Error fetching tasks:', error);
      // Use mock data if API fails
      let mockTasks = getMockTasks();
      
      // Filter mock data for Task Implementers
      if (isTaskImplementer && user?.name) {
        mockTasks = mockTasks.filter(task => task.assignee === user.name);
      }
      
      setTasks(mockTasks);
      setTotalCount(mockTasks.length);
    } finally {
      setLoading(false);
    }
  };

  const getMockTasks = (): Task[] => [
    {
      id: '1',
      title: 'Draft Privacy Policy Document',
      description: 'Create initial draft of website privacy policy covering all GDPR requirements',
      status: 'in-progress',
      priority: 'high',
      assignee: 'Jane Smith',
      dueDate: '2025-06-15',
      requirementId: 'req-1',
      requirementTitle: 'Develop Website Privacy Policy',
      createdAt: '2025-05-20T10:00:00Z',
      updatedAt: '2025-05-22T14:30:00Z'
    },
    {
      id: '2',
      title: 'Create Consent Form UI Component',
      description: 'Design and implement reusable consent form component for all data collection forms',
      status: 'pending',
      priority: 'medium',
      assignee: 'John Developer',
      dueDate: '2025-07-01',
      requirementId: 'req-2',
      requirementTitle: 'Implement Consent Form',
      createdAt: '2025-05-21T09:00:00Z',
      updatedAt: '2025-05-21T09:00:00Z'
    },
    {
      id: '3',
      title: 'Create Fire Equipment Register',
      description: 'Develop a comprehensive register of all fire equipment in the building',
      status: 'in-progress',
      priority: 'high',
      assignee: 'Robert Chen',
      dueDate: '2025-06-30',
      requirementId: 'req-3',
      requirementTitle: 'Fire Equipment Inspection Schedule',
      createdAt: '2025-05-19T11:00:00Z',
      updatedAt: '2025-05-22T16:00:00Z'
    },
    {
      id: '4',
      title: 'Request Elevator Maintenance Quotes',
      description: 'Contact three certified elevator maintenance companies for service agreement quotes',
      status: 'pending',
      priority: 'medium',
      assignee: 'Sarah Johnson',
      dueDate: '2025-07-15',
      requirementId: 'req-4',
      requirementTitle: 'Elevator Service Agreement',
      createdAt: '2025-05-18T14:00:00Z',
      updatedAt: '2025-05-18T14:00:00Z'
    },
    {
      id: '5',
      title: 'Implement Data Retention Policy',
      description: 'Configure automatic data deletion based on GDPR retention requirements',
      status: 'blocked',
      priority: 'critical',
      assignee: 'Tech Team',
      dueDate: '2025-06-01',
      requirementId: 'req-5',
      requirementTitle: 'Data Retention Compliance',
      createdAt: '2025-05-15T10:00:00Z',
      updatedAt: '2025-05-20T11:00:00Z'
    }
  ];

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleSearch = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'Enter') {
      fetchTasks();
    }
  };

  const getStatusColor = (status: Task['status']) => {
    switch (status) {
      case 'completed':
        return 'success';
      case 'in-progress':
        return 'primary';
      case 'blocked':
        return 'error';
      default:
        return 'default';
    }
  };

  const getPriorityColor = (priority: Task['priority']) => {
    switch (priority) {
      case 'critical':
        return 'error';
      case 'high':
        return 'warning';
      case 'medium':
        return 'primary';
      default:
        return 'default';
    }
  };

  const handleEdit = (taskId: string) => {
    navigate(`/tasks/${taskId}/edit`);
  };

  const handleDelete = async (taskId: string) => {
    if (window.confirm('Are you sure you want to delete this task?')) {
      try {
        await ApiService.delete<void>(`/tasks/${taskId}`);
        fetchTasks();
      } catch (error) {
        console.error('Error deleting task:', error);
      }
    }
  };

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4" component="h1" data-testid="task-list-heading">
          {isTaskImplementer ? 'My Tasks' : 'Tasks'}
        </Typography>
        {canCreate && (
          <Button
            id="create-task-button"
            variant="contained"
            color="primary"
            startIcon={<AddIcon />}
            onClick={() => navigate('/tasks/create')}
            data-testid="create-task-button"
          >
            Create Task
          </Button>
        )}
      </Box>

      <Paper sx={{ mb: 2, p: 2 }}>
        <TextField
          fullWidth
          variant="outlined"
          placeholder="Search tasks..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          onKeyPress={handleSearch}
          data-testid="task-search-input"
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
        />
      </Paper>

      <TableContainer component={Paper} data-testid="task-table-container">
        <Table data-testid="task-table">
          <TableHead>
            <TableRow>
              <TableCell>Title</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Priority</TableCell>
              <TableCell>Assignee</TableCell>
              <TableCell>Due Date</TableCell>
              <TableCell>Requirement</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={7} align="center" data-testid="task-loading">
                  Loading tasks...
                </TableCell>
              </TableRow>
            ) : tasks.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} align="center" data-testid="task-empty">
                  No tasks found
                </TableCell>
              </TableRow>
            ) : (
              tasks.map((task) => (
                <TableRow key={task.id} hover data-testid={`task-row-${task.id}`}>
                  <TableCell>
                    <Box display="flex" alignItems="center">
                      <TaskIcon sx={{ mr: 1, color: 'text.secondary' }} />
                      <Box>
                        <Typography variant="body1" data-testid={`task-title-${task.id}`}>{task.title}</Typography>
                        <Typography variant="body2" color="text.secondary" data-testid={`task-description-${task.id}`}>
                          {task.description.substring(0, 60)}...
                        </Typography>
                      </Box>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={task.status.replace('-', ' ')}
                      color={getStatusColor(task.status)}
                      size="small"
                      data-testid={`task-status-${task.id}`}
                    />
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={task.priority}
                      color={getPriorityColor(task.priority)}
                      size="small"
                      variant="outlined"
                      data-testid={`task-priority-${task.id}`}
                    />
                  </TableCell>
                  <TableCell data-testid={`task-assignee-${task.id}`}>{task.assignee}</TableCell>
                  <TableCell data-testid={`task-duedate-${task.id}`}>{new Date(task.dueDate).toLocaleDateString()}</TableCell>
                  <TableCell>
                    <Typography variant="body2" color="text.secondary" data-testid={`task-requirement-${task.id}`}>
                      {task.requirementTitle}
                    </Typography>
                  </TableCell>
                  <TableCell align="right">
                    <IconButton
                      size="small"
                      onClick={() => navigate(`/tasks/${task.id}`)}
                      title="View details"
                      data-testid={`task-view-${task.id}`}
                    >
                      <TaskIcon />
                    </IconButton>
                    {(hasPermission(Permission.TASK_EDIT) || 
                      (hasPermission(Permission.TASK_EDIT_OWN) && task.assignee === user?.name)) && (
                      <IconButton
                        size="small"
                        onClick={() => handleEdit(task.id)}
                        title="Edit task"
                        data-testid={`task-edit-${task.id}`}
                      >
                        <EditIcon />
                      </IconButton>
                    )}
                    {canDelete && (
                      <IconButton
                        size="small"
                        onClick={() => handleDelete(task.id)}
                        title="Delete task"
                        data-testid={`task-delete-${task.id}`}
                      >
                        <DeleteIcon />
                      </IconButton>
                    )}
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
        <TablePagination
          rowsPerPageOptions={[5, 10, 25]}
          component="div"
          count={totalCount}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          data-testid="task-pagination"
        />
      </TableContainer>
    </Box>
  );
}
